import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  Button,
  Card,
  Empty,
  Flex,
  Input,
  Modal,
  Select,
  Space,
  Spin,
  Tooltip,
  Typography,
  message,
  theme,
} from "antd";
import {
  ArrowLeftOutlined,
  CheckOutlined,
  LockOutlined,
  SearchOutlined,
  ShopOutlined,
  UsergroupAddOutlined,
} from "@ant-design/icons";
import { LateralModal } from "@/components/modals/LateralModal";
import { NormalModal } from "@/components/modals/NormalModal";
import { axiosInstance } from "@/utilities";
import { API_URL_PIM_CATALOG_ADMINS } from "@/services/XCONS_ENV";
import { debounce } from "@/utilities/debounce";
import { KeyRound, SellerIcon } from "@/components";
import {
  CargosDeOperador,
  CargosEnUnaLinea,
  claveDeCargo,
} from "./CargosDeOperador";
import type {
  CatalogAdmin,
  CatalogAdminCandidate,
  CatalogAdmins,
} from "./types";
import { NIVEL, NIVEL_PARA_TODO } from "@/utilities/niveles-de-permiso";

const { Text } = Typography;

/**
 * Los cinco valores, de mayor a menor como en Operadores del Store. Admin, Editor y Lector
 * llevan el "para todo" porque acá valen para el catálogo entero (xcons-docs#587); en un
 * producto no, y por eso `ProductParticipantsDrawer` usa los nombres pelados.
 *
 * `hint` es opcional: el selector de este panel va como en toda la plataforma, sólo con el
 * nombre del nivel (xcons-docs#547).
 */
export type Nivel = { value: string; label: string; hint?: string };

const NIVELES: Nivel[] = [
  { value: "admin", label: NIVEL_PARA_TODO.admin },
  { value: "editor", label: NIVEL_PARA_TODO.editor },
  { value: "viewer", label: NIVEL_PARA_TODO.viewer },
  { value: "by_relation", label: NIVEL.by_relation },
];

/** Cuántas filas suma cada tramo del scroll infinito. */
const TRAMO = 30;

/** El valor de la opción "Remover" del selector: no es un nivel, dispara el quitar. */
export const REMOVER = "remove";

/**
 * **Administradores del catálogo**: quién tiene el permiso global, y el panel donde se
 * concede y se quita.
 *
 * Es la contracara del drawer de participantes de un producto. Allá el alcance es un
 * producto y el nivel puede ser Editor o Admin; acá el alcance es el catálogo entero y el
 * nivel es uno de los cuatro de `NIVELES`. **Acá se da el acceso al Catálogo**: ya no está
 * en el panel de Operadores del Store, porque el catálogo no es de ninguna tienda.
 *
 * **Los controles se muestran deshabilitados y explicados, no escondidos** (R11): quien
 * entra al Catálogo ve quiénes administran aunque no pueda cambiarlo. `canManage` lo
 * resuelve el backend, que es el único que conoce la regla.
 *
 * **Sin props de open/close:** `LateralModal` sin `hideTrigger` maneja su propio trigger
 * (xcons-docs#366, punto 11) — el `Drawer` interno usa `destroyOnClose`, así que este
 * componente se desmonta al cerrar y `refresh()` en el mount reemplaza al viejo
 * `if (open) refresh()`.
 */
const CatalogAdminsPanel: React.FC = () => {
  const [data, setData] = useState<CatalogAdmins | null>(null);
  const [loading, setLoading] = useState(false);
  const [guardando, setGuardando] = useState(false);
  const [candidatos, setCandidatos] = useState<CatalogAdminCandidate[]>([]);
  const [buscando, setBuscando] = useState(false);
  const [agregando, setAgregando] = useState(false);
  const [busqueda, setBusqueda] = useState("");
  // Cuántas filas se pintan: el scroll infinito suma de a `TRAMO` al llegar al fondo.
  const [visibles, setVisibles] = useState(TRAMO);
  const { token } = theme.useToken();

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const { data: res } = await axiosInstance.get<CatalogAdmins>(
        API_URL_PIM_CATALOG_ADMINS,
      );
      setData(res);
    } catch {
      message.error("No se pudieron cargar los administradores del catálogo");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const buscarCandidatos = useMemo(
    () =>
      debounce(async (texto: string) => {
        setBuscando(true);
        try {
          const { data: res } = await axiosInstance.get<
            CatalogAdminCandidate[]
          >(`${API_URL_PIM_CATALOG_ADMINS}/candidates`, {
            params: { q: texto },
          });
          setCandidatos(res ?? []);
        } catch {
          setCandidatos([]);
        } finally {
          setBuscando(false);
        }
      }, 350),
    [],
  );

  const admins = data?.admins ?? [];
  const canManage = Boolean(data?.canManage);

  /**
   * El backend devuelve la lista nueva en la misma respuesta: no hace falta refetch. El panel
   * de Permisos no lleva historial (Diseño, 2026-10-01).
   */
  const aplicar = (res: CatalogAdmins) => setData(res);

  const error = (e: unknown, fallback: string) => {
    const res = (e as { response?: { data?: { message?: string } } })?.response;
    message.error(res?.data?.message ?? fallback);
  };

  const agregar = async (candidato: CatalogAdminCandidate, level: string) => {
    setGuardando(true);
    try {
      const { data: res } = await axiosInstance.post<CatalogAdmins>(
        API_URL_PIM_CATALOG_ADMINS,
        { userId: candidato.userId, level },
      );
      aplicar(res);
      setCandidatos([]);
      setAgregando(false);
      message.success(`${candidato.name} ya tiene acceso al Catálogo`);
    } catch (e) {
      error(e, "No se pudo dar acceso al Catálogo");
    } finally {
      setGuardando(false);
    }
  };

  const cambiarNivel = async (admin: CatalogAdmin, level: string) => {
    setGuardando(true);
    try {
      const { data: res } = await axiosInstance.post<CatalogAdmins>(
        API_URL_PIM_CATALOG_ADMINS,
        { userId: admin.userId, level },
      );
      aplicar(res);
      message.success(`${admin.name}: ${nombreDeNivel(level)}`);
    } catch (e) {
      error(e, "No se pudo cambiar el nivel");
    } finally {
      setGuardando(false);
    }
  };

  const quitar = async (admin: CatalogAdmin) => {
    setGuardando(true);
    try {
      const { data: res } = await axiosInstance.delete<CatalogAdmins>(
        `${API_URL_PIM_CATALOG_ADMINS}/${admin.userId}`,
      );
      aplicar(res);
      message.success(`${admin.name} ya no es Admin de catálogo`);
    } catch (e) {
      error(e, "No se pudo quitar el Admin de catálogo");
    } finally {
      setGuardando(false);
    }
  };

  /**
   * Cambiar el nivel se confirma, como Remover (xcons-docs#547): el nivel vale para todo el
   * Catálogo, y la fila salta a otra sección recién al confirmar.
   */
  const confirmarNivel = (admin: CatalogAdmin, level: string) =>
    Modal.confirm({
      title: `¿Cambiar el nivel de ${admin.name}?`,
      content: `Pasa de ${nombreDeNivel(
        admin.level ?? "admin",
      )} a ${nombreDeNivel(level)} en todo el Catálogo.`,
      okText: "Cambiar",
      cancelText: "Cancelar",
      onOk: () => cambiarNivel(admin, level),
    });

  const confirmarQuitar = (admin: CatalogAdmin) =>
    Modal.confirm({
      title: "¿Remover el acceso al Catálogo?",
      content: `${admin.name} pierde el nivel que tiene sobre todo el catálogo.`,
      okText: "Remover",
      okType: "danger",
      cancelText: "Cancelar",
      onOk: () => quitar(admin),
    });

  /**
   * Una fila por persona (xcons-docs#547): a la izquierda el nombre y debajo sus stores y
   * sellers, que sólo informan y llegan hasta casi el selector; a la derecha el selector,
   * centrado en el alto de las dos líneas. Una sola fila aunque opere en varios lugares: el
   * nivel es uno solo por persona.
   */
  const fila = (admin: CatalogAdmin) => (
    <Flex align="center" gap={12} style={{ width: "100%", minWidth: 0 }}>
      <Flex vertical style={{ flex: 1, minWidth: 0 }}>
        <Flex align="center" gap={8} wrap="wrap" style={{ minWidth: 0 }}>
          <Text strong style={{ fontSize: 12 }}>
            {admin.name}
          </Text>
          {admin.isSelf && <Text type="secondary">(Vos)</Text>}
        </Flex>
        <CargosEnUnaLinea permissions={admin.permissions} />
      </Flex>
      {/* Anti-lockout: bajarse o quitarse a uno mismo dejaría al catálogo sin
            Admin y de vuelta al INSERT de ops. El servidor lo rechaza igual —
            esto sólo evita el 403 innecesario. Sin `canManage` el selector se
            ve deshabilitado, no escondido (R11). */}
      <Tooltip
        title={
          canManage && admin.isSelf
            ? "No podés cambiar ni quitar tu propio acceso: pedíselo a otro Admin"
            : undefined
        }
      >
        <Select<string>
          size="small"
          style={{ width: 150, flexShrink: 0 }}
          value={admin.level ?? "admin"}
          loading={guardando}
          disabled={!canManage || admin.isSelf}
          onChange={(value) =>
            value === REMOVER
              ? confirmarQuitar(admin)
              : confirmarNivel(admin, value)
          }
          options={[
            ...NIVELES.map((n) => ({ value: n.value, label: n.label })),
            // Quitar es una opción más del selector, al final y en rojo,
            // como en el panel de Permisos de Presupuestos y Pedidos: el
            // rojo lo pone `config.css`, que matchea el `title`.
            { value: REMOVER, label: "Remover", title: "Remover" },
          ]}
        />
      </Tooltip>
    </Flex>
  );

  /**
   * Las personas que se ven, ya ordenadas por nivel y por nombre.
   *
   * **Sólo quien comparte un store o seller con quien mira** (xcons-docs#547): la API ya
   * manda los cargos recortados a ese alcance, así que a quien no le queda ninguno no se lo
   * muestra. Con un API anterior a xcons-docs#402, que no manda cargos, se ven todos.
   */
  const ordenados = useMemo(() => {
    const texto = busqueda.trim().toLowerCase();
    const orden = (a: CatalogAdmin) =>
      NIVELES.findIndex((n) => n.value === (a.level ?? "admin"));
    return admins
      .filter((a) => a.permissions === undefined || a.permissions.length > 0)
      .filter(
        (a) =>
          !texto ||
          a.name.toLowerCase().includes(texto) ||
          (a.email ?? "").toLowerCase().includes(texto),
      )
      .sort((a, b) => orden(a) - orden(b) || a.name.localeCompare(b.name));
  }, [admins, busqueda]);

  // Cada búsqueda arranca de nuevo desde el primer tramo.
  useEffect(() => setVisibles(TRAMO), [busqueda]);

  /** Cuántas personas tiene cada nivel, sobre la lista entera y no sobre lo pintado. */
  const porNivel = useMemo(() => {
    const cuenta = new Map<string, number>();
    for (const a of ordenados) {
      const nivel = a.level ?? "admin";
      cuenta.set(nivel, (cuenta.get(nivel) ?? 0) + 1);
    }
    return cuenta;
  }, [ordenados]);

  const alScrollear = (e: React.UIEvent<HTMLDivElement>) => {
    const el = e.currentTarget;
    if (
      visibles < ordenados.length &&
      el.scrollTop + el.clientHeight >= el.scrollHeight - 80
    )
      setVisibles((v) => v + TRAMO);
  };

  const borde = `1px solid ${token.colorBorderSecondary}`;

  return (
    // `LateralModal` no le pone padding al body (lo decide cada panel): acá va
    // "16px 24px", el mismo que usa el resto de los paneles de Permisos
    // (`components/permissions-lateral/index.tsx`) — sin esto el contenido queda
    // pegado al borde izquierdo del panel, no de la pantalla.
    // El panel no scrollea entero: queda fija la barra de arriba y scrollea la tabla
    // (xcons-docs#547).
    <div
      style={{
        flex: 1,
        minHeight: 0,
        display: "flex",
        flexDirection: "column",
        gap: 16,
        padding: "16px 24px",
      }}
    >
      {/* Sin aviso para quien no administra (xcons-docs#479: sin banners informativos): los selectores
          deshabilitados ya dicen que no puede cambiar la lista. */}

      {/* Buscar y Agregar en la misma línea y con la misma altura. Agregar abre un popup,
          como el botón de Presupuestos y Pedidos (`QuotePermissionsLateral`). */}
      <Flex gap={8} align="center">
        <Input
          size="small"
          allowClear
          placeholder="Buscar por nombre o email"
          suffix={
            <SearchOutlined style={{ color: token.colorTextQuaternary }} />
          }
          value={busqueda}
          onChange={(e) => setBusqueda(e.target.value)}
          style={{ flex: 1, height: 24 }}
        />
        {canManage && (
          <Button
            size="small"
            shape="round"
            icon={<UsergroupAddOutlined />}
            onClick={() => setAgregando(true)}
          >
            Agregar
          </Button>
        )}
      </Flex>

      <NormalModal
        footer={null}
        destroyOnClose
        open={agregando}
        onCancel={() => setAgregando(false)}
        title="Agregar operador"
      >
        {/* Sin elegir por cuál store o seller se lo suma, como en Participantes del
            producto: el acceso al Catálogo es uno por persona (xcons-docs#547). */}
        <AgregarOperador
          niveles={NIVELES}
          nivelInicial="by_relation"
          elegirCargo={false}
          candidatos={candidatos.filter(
            (c) => !admins.some((a) => a.userId === c.userId),
          )}
          buscando={buscando}
          guardando={guardando}
          onSearch={buscarCandidatos}
          onVolver={() => setAgregando(false)}
          onGuardar={agregar}
        />
      </NormalModal>

      {loading ? (
        <Flex justify="center" style={{ padding: 24 }}>
          <Spin />
        </Flex>
      ) : admins.length ? (
        // La tabla agrupada por nivel (xcons-docs#547): el encabezado fijo arriba, y cada
        // nivel arranca con su fila —más baja que el encabezado, en el verde suave del
        // tema— seguida de sus personas.
        <Card
          size="small"
          // Como las cards de la plataforma (Historial de Presupuestos): el ícono y el
          // título en el encabezado de la Card. El ícono es el de Operadores del menú del
          // store. El encabezado no scrollea: la barra arranca debajo.
          title={
            <Flex gap={4} align="center">
              <KeyRound style={{ fontSize: 14 }} />
              <Text style={{ fontSize: 14, fontWeight: 600 }}>Operadores</Text>
            </Flex>
          }
          style={{
            flex: 1,
            minHeight: 0,
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
          }}
          styles={{
            body: { padding: 0, flex: 1, minHeight: 0, display: "flex" },
          }}
        >
          <div
            onScroll={alScrollear}
            style={{
              flex: 1,
              minWidth: 0,
              minHeight: 0,
              overflowY: "auto",
              overflowX: "hidden",
            }}
          >
            {ordenados.length ? (
              ordenados.slice(0, visibles).map((admin, i, pintados) => {
                const nivel = admin.level ?? "admin";
                const arranca =
                  i === 0 || (pintados[i - 1].level ?? "admin") !== nivel;
                return (
                  <React.Fragment key={admin.userId}>
                    {arranca && (
                      <div
                        style={{
                          padding: "2px 16px",
                          background: token.colorPrimaryBg,
                          borderBottom: borde,
                          fontSize: 11,
                          lineHeight: "18px",
                          fontWeight: 600,
                          color: token.colorTextSecondary,
                        }}
                      >
                        {nombreDeNivel(nivel)} ({porNivel.get(nivel) ?? 0})
                      </div>
                    )}
                    <div style={{ padding: "6px 16px", borderBottom: borde }}>
                      {fila(admin)}
                    </div>
                  </React.Fragment>
                );
              })
            ) : (
              <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description={
                  busqueda.trim()
                    ? `Nadie coincide con “${busqueda.trim()}”`
                    : "Nadie comparte un store o seller con vos"
                }
              />
            )}
            {visibles < ordenados.length && (
              <Flex justify="center" style={{ padding: 12 }}>
                <Spin size="small" />
              </Flex>
            )}
          </div>
        </Card>
      ) : (
        <Empty
          description={
            <Space direction="vertical" size={0}>
              <Text>Todavía nadie tiene acceso al Catálogo</Text>
              <Text type="secondary" style={{ fontSize: 12 }}>
                El primer Admin lo crea el equipo técnico en cada ambiente;
                desde acá se da acceso al resto.
              </Text>
            </Space>
          }
        />
      )}
    </div>
  );
};

const nombreDeNivel = (level: string) =>
  NIVELES.find((n) => n.value === level)?.label ?? level;

/**
 * El contenido del popup de Agregar, con el mismo armado que `PermissionsSelect` de
 * Presupuestos: buscar, una tarjeta con el elegido y su nivel, y Volver / Guardar. No se
 * reusa ése porque busca contactos de un store: acá los candidatos son sólo quienes ya
 * entran al Catálogo —nombrar a alguien sin el permiso de sección le daría uno que no
 * puede ejercer—.
 *
 * **Es el mismo popup para el Catálogo, para un producto y para Asignar dueño en lote**
 * (xcons-docs#409, #462): cambian los niveles que ofrece y con cuál arranca, y nada más.
 */
export const AgregarOperador = <C extends CatalogAdminCandidate>({
  niveles,
  nivelInicial,
  candidatos,
  buscando,
  guardando,
  onSearch,
  onVolver,
  onGuardar,
  elegirCargo = true,
}: {
  niveles: Nivel[];
  /** El más bajo que sirve para trabajar: nadie queda Admin sin que alguien lo elija. */
  nivelInicial: string;
  candidatos: C[];
  buscando: boolean;
  guardando: boolean;
  onSearch: (texto: string) => void;
  onVolver: () => void;
  onGuardar: (candidato: C, level: string) => void;
  /**
   * Si se elige por cuál store o seller se suma a la persona. Lo apaga Participantes del
   * producto (xcons-docs#466): ahí se suma a la persona sin importar desde dónde, y sus
   * stores y sellers van como lista que sólo informa.
   */
  elegirCargo?: boolean;
}) => {
  const [elegido, setElegido] = useState<C | null>(null);
  // Por cuál de sus stores o sellers se lo elige: el tag en verde. Sólo de pantalla —el
  // permiso es global por persona—, pero distingue al que está dado de alta en varios.
  const [cargo, setCargo] = useState<string | null>(null);
  // El tag clickeado en el desplegable. El click llega primero al tag y después a la
  // opción, que es la que dispara `onChange`: ahí se lee y se consume.
  const tagClickeado = useRef<string | null>(null);
  const [nivel, setNivel] = useState(nivelInicial);
  const cargoElegido =
    elegido?.permissions?.find((c) => claveDeCargo(c) === cargo) ?? null;

  return (
    // Alto fijo y botones al pie, como `PermissionsSelect`: el popup no salta al elegir.
    <Flex vertical gap={10} style={{ minHeight: 450 }}>
      <Select
        showSearch
        style={{ width: "100%" }}
        placeholder="Ingresar nombre o email"
        suffixIcon={<SearchOutlined />}
        filterOption={false}
        disabled={guardando}
        notFoundContent={
          buscando ? (
            <Spin size="small" />
          ) : (
            <Text type="secondary" style={{ fontSize: 12 }}>
              Sin resultados con acceso al Catálogo
            </Text>
          )
        }
        onSearch={onSearch}
        onChange={(userId) => {
          const candidato = candidatos.find((c) => c.userId === userId) ?? null;
          const primero = candidato?.permissions?.[0];
          setElegido(candidato);
          setCargo(
            tagClickeado.current ?? (primero ? claveDeCargo(primero) : null),
          );
          tagClickeado.current = null;
        }}
        value={elegido?.userId ?? null}
        options={candidatos.map((c) => ({
          value: c.userId,
          label: c.name,
          email: c.email,
          permissions: c.permissions,
        }))}
        // Cada resultado con nombre, mail y sus stores y sellers como tags (xcons-docs#383).
        // Clickear un tag elige a la persona por ese lugar; el resto de la fila, por el
        // primero.
        optionRender={(option) => (
          <Flex vertical style={{ padding: "4px 0" }}>
            <Text strong>{option.data.label}</Text>
            {option.data.email && (
              <Text type="secondary" style={{ fontSize: 12 }}>
                {option.data.email}
              </Text>
            )}
            {elegirCargo ? (
              <CargosDeOperador
                permissions={option.data.permissions}
                seleccionado={
                  option.data.value === elegido?.userId ? cargo : null
                }
                onSeleccionar={(c) => {
                  tagClickeado.current = claveDeCargo(c);
                }}
              />
            ) : (
              <CargosDeOperador lista permissions={option.data.permissions} />
            )}
          </Flex>
        )}
      />

      {/* El elegido como una fila de la lista del panel (xcons-docs#409): la tarjeta con
          el store o seller por el que se lo eligió en el encabezado, y adentro el nombre
          y el nivel. Los demás cargos ya se vieron en el buscador; para cambiarlo se lo
          vuelve a elegir ahí. */}
      {elegido && (
        <Card
          className="drag-list-card"
          title={
            elegirCargo && cargoElegido ? (
              <Flex align="center" gap={8}>
                {cargoElegido.seller ? <SellerIcon /> : <ShopOutlined />}
                <Text strong>
                  {cargoElegido.name?.trim() ||
                    cargoElegido.seller ||
                    cargoElegido.store}
                </Text>
              </Flex>
            ) : null
          }
        >
          <Flex
            vertical
            className="customer-item"
            style={{ padding: "5px 12px" }}
          >
            <Flex justify="space-between" align="center" gap={8}>
              <Text strong style={{ fontSize: 12 }}>
                {elegido.name}
              </Text>
              {/* Con un solo nivel se muestra igual, pero fijo: Asignar dueño siempre deja
                  Admin (xcons-docs#462). */}
              <Select<string>
                size="small"
                style={{ width: 150, flexShrink: 0 }}
                disabled={niveles.length === 1}
                value={nivel}
                onChange={setNivel}
                options={niveles.map((n) => ({
                  value: n.value,
                  label: n.label,
                  title: n.hint,
                }))}
              />
            </Flex>
            {/* Sin elegir cargo, sus stores y sellers van abajo, como en el panel. */}
            {!elegirCargo && (
              <CargosDeOperador lista permissions={elegido.permissions} />
            )}
          </Flex>
        </Card>
      )}

      <Flex
        justify="space-between"
        gap={8}
        style={{ marginTop: "auto", paddingTop: 20 }}
      >
        <Button icon={<ArrowLeftOutlined />} onClick={onVolver}>
          Volver
        </Button>
        <Button
          type="primary"
          icon={<CheckOutlined />}
          iconPosition="end"
          loading={guardando}
          disabled={!elegido}
          onClick={() => elegido && onGuardar(elegido, nivel)}
        >
          Guardar
        </Button>
      </Flex>
    </Flex>
  );
};

/** El trigger: texto "Permisos" + candado, mismo patrón que Presupuestos/Pedidos
 * (`QuotePermissionsLateral`, `PermissionsLateral`) — arriba a la derecha, por fuera de
 * la tabla. */
export const CatalogAdminsDrawer: React.FC = () => (
  <LateralModal
    title="Permisos"
    buttonSize="small"
    buttonType="text"
    buttonName="Permisos"
    iconPosition="end"
    iconSvg={<LockOutlined />}
  >
    <CatalogAdminsPanel />
  </LateralModal>
);
